// 🔒 SECURE Multi-Layer Cache Manager for AccToo
// Implements security-first caching with encryption and data classification

import CryptoJS from 'crypto-js'; // You'll need to: npm install crypto-js

// Security classification levels
const DataSensitivity = {
  PUBLIC: 'public',           // Safe to cache anywhere (company name, display preferences)
  INTERNAL: 'internal',       // Cache in memory only (dashboard summaries, list data)
  CONFIDENTIAL: 'confidential', // Must encrypt before caching (company details, party info)
  RESTRICTED: 'restricted'    // NEVER cache (tokens, passwords, bank details, PAN, etc.)
};

// Define what data types are safe to cache
const DATA_CLASSIFICATION = {
  // NEVER CACHE THESE (RESTRICTED)
  auth_token: DataSensitivity.RESTRICTED,
  access_token: DataSensitivity.RESTRICTED,
  refresh_token: DataSensitivity.RESTRICTED,
  mpin: DataSensitivity.RESTRICTED,
  password: DataSensitivity.RESTRICTED,
  api_key: DataSensitivity.RESTRICTED,
  session_id: DataSensitivity.RESTRICTED,
  bank_account_full: DataSensitivity.RESTRICTED,
  credit_card: DataSensitivity.RESTRICTED,
  pan_number: DataSensitivity.RESTRICTED,
  aadhaar_number: DataSensitivity.RESTRICTED,
  
  // ENCRYPT BEFORE CACHING (CONFIDENTIAL)
  company_details: DataSensitivity.CONFIDENTIAL,
  party_details: DataSensitivity.CONFIDENTIAL,
  employee_details: DataSensitivity.CONFIDENTIAL,
  transaction_details: DataSensitivity.CONFIDENTIAL,
  financial_reports: DataSensitivity.CONFIDENTIAL,
  
  // MEMORY ONLY (INTERNAL)
  dashboard_summary: DataSensitivity.INTERNAL,
  sales_list: DataSensitivity.INTERNAL,
  purchase_list: DataSensitivity.INTERNAL,
  party_list: DataSensitivity.INTERNAL,
  item_list: DataSensitivity.INTERNAL,
  
  // SAFE TO CACHE (PUBLIC)
  user_display_name: DataSensitivity.PUBLIC,
  company_name: DataSensitivity.PUBLIC,
  ui_preferences: DataSensitivity.PUBLIC,
  theme_settings: DataSensitivity.PUBLIC,
  language_preference: DataSensitivity.PUBLIC
};

class SecureCacheManager {
  constructor() {
    // Layer 1: In-memory cache (fastest, most secure - cleared on page close)
    this.memoryCache = new Map();
    
    // Layer 2: Session storage (survives page refresh, cleared on tab close)
    this.sessionCache = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    
    // Layer 3: Local storage (survives browser restart - encrypted data only)
    this.persistentCache = typeof localStorage !== 'undefined' ? localStorage : null;
    
    // Encryption key (should be unique per user session)
    this.encryptionKey = null;
    
    // Default TTL for different sensitivity levels
    this.cacheTTL = {
      [DataSensitivity.PUBLIC]: 60 * 60 * 1000,      // 1 hour
      [DataSensitivity.INTERNAL]: 5 * 60 * 1000,     // 5 minutes
      [DataSensitivity.CONFIDENTIAL]: 2 * 60 * 1000, // 2 minutes
      [DataSensitivity.RESTRICTED]: 0                 // Never cache
    };
    
    // Statistics
    this.stats = {
      hits: 0,
      misses: 0,
      securityBlocks: 0,
      encryptionOps: 0
    };
    
    // Initialize encryption key
    this._initializeEncryption();
    
    // Auto-cleanup on logout
    this._setupSecurityListeners();
  }

  /**
   * Initialize encryption key from user session
   * @private
   */
  _initializeEncryption() {
    try {
      // Generate session-based encryption key
      // In production, derive this from user's session token (but don't store the token!)
      const sessionId = this._getSessionId();
      this.encryptionKey = CryptoJS.SHA256(sessionId + 'acctoo-secure-cache').toString();
    } catch (e) {
      console.warn('Failed to initialize encryption:', e);
      this.encryptionKey = 'fallback-key-change-this'; // Fallback (not secure)
    }
  }

  /**
   * Get or generate session ID (NOT the auth token!)
   * @private
   */
  _getSessionId() {
    let sessionId = sessionStorage.getItem('cache_session_id');
    if (!sessionId) {
      sessionId = this._generateSecureId();
      sessionStorage.setItem('cache_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Generate secure random ID
   * @private
   */
  _generateSecureId() {
    const array = new Uint8Array(32);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }
    // Fallback
    return Math.random().toString(36).substr(2) + Date.now().toString(36);
  }

  /**
   * Setup security event listeners
   * @private
   */
  _setupSecurityListeners() {
    // Clear cache on logout
    if (typeof window !== 'undefined') {
      window.addEventListener('user_logout', () => {
        console.log('🔒 Logout detected - clearing all caches');
        this.clearAll();
      });
      
      // Clear on window close
      window.addEventListener('beforeunload', () => {
        // Clear sensitive data from memory
        this.clearSensitiveData();
      });
    }
  }

  /**
   * Classify data sensitivity based on key
   * @private
   */
  _classifyData(key) {
    // Check explicit classification
    for (const [pattern, sensitivity] of Object.entries(DATA_CLASSIFICATION)) {
      if (key.includes(pattern)) {
        return sensitivity;
      }
    }
    
    // Check for restricted keywords
    const restrictedKeywords = ['token', 'password', 'secret', 'key', 'mpin', 'pan', 'aadhaar', 'ssn', 'credit'];
    if (restrictedKeywords.some(keyword => key.toLowerCase().includes(keyword))) {
      return DataSensitivity.RESTRICTED;
    }
    
    // Default to internal (memory only)
    return DataSensitivity.INTERNAL;
  }

  /**
   * Encrypt data using AES
   * @private
   */
  _encrypt(data) {
    try {
      this.stats.encryptionOps++;
      const jsonStr = JSON.stringify(data);
      const encrypted = CryptoJS.AES.encrypt(jsonStr, this.encryptionKey).toString();
      return encrypted;
    } catch (e) {
      console.error('Encryption failed:', e);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt data
   * @private
   */
  _decrypt(encryptedData) {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      const jsonStr = decrypted.toString(CryptoJS.enc.Utf8);
      return JSON.parse(jsonStr);
    } catch (e) {
      console.error('Decryption failed:', e);
      return null;
    }
  }

  /**
   * Sanitize data before caching (remove sensitive fields)
   * @private
   */
  _sanitizeData(data) {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = Array.isArray(data) ? [...data] : { ...data };
    
    // List of fields to NEVER cache
    const sensitiveFields = [
      'authToken', 'accessToken', 'refreshToken', 'token',
      'password', 'mpin', 'pin', 'secret',
      'apiKey', 'privateKey', 'sessionId',
      'bankAccountNumber', 'accountNumber',
      'creditCard', 'cardNumber', 'cvv',
      'panNumber', 'pan', 'aadhaar', 'ssn',
      'otp', 'verificationCode'
    ];
    
    // Recursively remove sensitive fields
    const sanitizeObj = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;
      
      if (Array.isArray(obj)) {
        return obj.map(item => sanitizeObj(item));
      }
      
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        // Skip sensitive fields
        if (sensitiveFields.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
          console.warn(`🔒 Blocked caching of sensitive field: ${key}`);
          continue;
        }
        
        // Recursively sanitize nested objects
        result[key] = typeof value === 'object' ? sanitizeObj(value) : value;
      }
      return result;
    };
    
    return sanitizeObj(sanitized);
  }

  /**
   * Get data from cache with security checks
   * @param {string} key - Cache key
   * @param {Function} fetchFn - Function to fetch data if cache miss
   * @param {number} ttl - Time to live in milliseconds (optional)
   * @param {Object} options - Additional options
   * @returns {Promise<any>}
   */
  async get(key, fetchFn, ttl = null, options = {}) {
    // Classify data sensitivity
    const sensitivity = this._classifyData(key);
    
    // SECURITY CHECK: Never cache restricted data
    if (sensitivity === DataSensitivity.RESTRICTED) {
      this.stats.securityBlocks++;
      console.warn(`🔒 SECURITY: Blocked caching of restricted data: ${key}`);
      // Always fetch fresh for restricted data
      return await fetchFn();
    }
    
    // Get appropriate TTL for sensitivity level
    const effectiveTTL = ttl || this.cacheTTL[sensitivity];
    
    // 1. Check memory cache (always check, all sensitivities)
    const memCached = this.memoryCache.get(key);
    if (memCached && this._isValid(memCached, effectiveTTL)) {
      this.stats.hits++;
      console.log('✅ Cache HIT (memory):', key);
      return memCached.data;
    }

    // 2. Check session storage (for PUBLIC and INTERNAL data)
    if (sensitivity !== DataSensitivity.CONFIDENTIAL && this.sessionCache) {
      try {
        const sessionCached = this.sessionCache.getItem(key);
        if (sessionCached) {
          const parsed = JSON.parse(sessionCached);
          if (this._isValid(parsed, effectiveTTL)) {
            this.stats.hits++;
            console.log('✅ Cache HIT (session):', key);
            this.memoryCache.set(key, parsed);
            return parsed.data;
          }
        }
      } catch (e) {
        console.warn('Session cache read error:', e);
      }
    }

    // 3. Check localStorage (ONLY for PUBLIC data, encrypted for CONFIDENTIAL)
    if (sensitivity === DataSensitivity.PUBLIC || sensitivity === DataSensitivity.CONFIDENTIAL) {
      if (this.persistentCache) {
        try {
          const persistCached = this.persistentCache.getItem(key);
          if (persistCached) {
            let parsed;
            
            // Decrypt if confidential
            if (sensitivity === DataSensitivity.CONFIDENTIAL) {
              const decrypted = this._decrypt(persistCached);
              if (decrypted && this._isValid(decrypted, effectiveTTL)) {
                parsed = decrypted;
              }
            } else {
              parsed = JSON.parse(persistCached);
              if (!this._isValid(parsed, effectiveTTL)) {
                parsed = null;
              }
            }
            
            if (parsed) {
              this.stats.hits++;
              console.log('✅ Cache HIT (persistent):', key);
              this.memoryCache.set(key, parsed);
              return parsed.data;
            }
          }
        } catch (e) {
          console.warn('Persistent cache read error:', e);
        }
      }
    }

    // 4. Cache MISS - Fetch fresh data
    this.stats.misses++;
    console.log('❌ Cache MISS - Fetching:', key);
    
    try {
      const data = await fetchFn();
      
      // Sanitize before caching
      const sanitizedData = this._sanitizeData(data);
      
      // Cache with appropriate security
      this.set(key, sanitizedData, effectiveTTL, { sensitivity });
      
      return data; // Return original data (not sanitized) to caller
    } catch (error) {
      console.error('Fetch error for key:', key, error);
      throw error;
    }
  }

  /**
   * Set data in cache with security
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live
   * @param {Object} options - Additional options
   */
  set(key, data, ttl = 5 * 60 * 1000, options = {}) {
    const sensitivity = options.sensitivity || this._classifyData(key);
    
    // SECURITY CHECK: Never cache restricted data
    if (sensitivity === DataSensitivity.RESTRICTED) {
      this.stats.securityBlocks++;
      console.warn(`🔒 SECURITY: Blocked caching of restricted data: ${key}`);
      return;
    }
    
    // Sanitize data
    const sanitizedData = this._sanitizeData(data);
    
    const cacheEntry = {
      data: sanitizedData,
      timestamp: Date.now(),
      ttl,
      sensitivity,
      version: '2.0-secure'
    };
    
    // Always store in memory (most secure)
    this.memoryCache.set(key, cacheEntry);
    
    // Store in session storage (for PUBLIC and INTERNAL)
    if (sensitivity !== DataSensitivity.CONFIDENTIAL && this.sessionCache) {
      try {
        this.sessionCache.setItem(key, JSON.stringify(cacheEntry));
      } catch (e) {
        console.warn('Session storage error:', e);
      }
    }
    
    // Store in localStorage (ONLY PUBLIC or encrypted CONFIDENTIAL)
    if (this.persistentCache) {
      try {
        if (sensitivity === DataSensitivity.PUBLIC) {
          // Store public data unencrypted
          this.persistentCache.setItem(key, JSON.stringify(cacheEntry));
        } else if (sensitivity === DataSensitivity.CONFIDENTIAL) {
          // Encrypt confidential data before storing
          const encrypted = this._encrypt(cacheEntry);
          this.persistentCache.setItem(key, encrypted);
        }
        // INTERNAL data is NOT stored in localStorage
      } catch (e) {
        console.warn('Persistent storage error:', e);
      }
    }
    
    console.log(`💾 Securely cached: ${key} (${sensitivity}, TTL: ${ttl}ms)`);
  }

  /**
   * Check if cache entry is still valid
   * @private
   */
  _isValid(cacheEntry, ttl) {
    if (!cacheEntry || !cacheEntry.timestamp) return false;
    const age = Date.now() - cacheEntry.timestamp;
    return age < ttl;
  }

  /**
   * Invalidate a specific cache key
   */
  invalidate(key) {
    this.memoryCache.delete(key);
    
    if (this.sessionCache) {
      try {
        this.sessionCache.removeItem(key);
      } catch (e) {}
    }
    
    if (this.persistentCache) {
      try {
        this.persistentCache.removeItem(key);
      } catch (e) {}
    }
    
    console.log('🗑️ Invalidated cache:', key);
  }

  /**
   * Invalidate all keys matching a pattern
   * @param {string|RegExp} pattern - Pattern to match keys
   */
  invalidatePattern(pattern) {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    let count = 0;
    
    // Clear from memory cache
    for (const key of this.memoryCache.keys()) {
      if (regex.test(key)) {
        this.memoryCache.delete(key);
        count++;
      }
    }
    
    // Clear from session storage
    if (this.sessionCache) {
      try {
        const keys = Object.keys(this.sessionCache);
        for (const key of keys) {
          if (regex.test(key)) {
            this.sessionCache.removeItem(key);
          }
        }
      } catch (e) {
        console.warn('Session cache pattern clear error:', e);
      }
    }
    
    // Clear from localStorage
    if (this.persistentCache) {
      try {
        const keys = Object.keys(this.persistentCache);
        for (const key of keys) {
          if (regex.test(key)) {
            this.persistentCache.removeItem(key);
          }
        }
      } catch (e) {
        console.warn('Persistent cache pattern clear error:', e);
      }
    }
    
    console.log(`🗑️ Invalidated ${count} cache entries matching pattern:`, pattern);
  }

  /**
   * Clear all caches (use on logout!)
   */
  clearAll() {
    console.log('🔒 SECURITY: Clearing ALL caches');
    
    this.memoryCache.clear();
    
    if (this.sessionCache) {
      try {
        this.sessionCache.clear();
      } catch (e) {}
    }
    
    if (this.persistentCache) {
      try {
        // Clear only our cache keys
        const keys = Object.keys(this.persistentCache);
        keys.forEach(key => {
          if (key.startsWith('user_') || key.startsWith('company_') || 
              key.startsWith('dashboard_') || key.startsWith('cache_')) {
            this.persistentCache.removeItem(key);
          }
        });
      } catch (e) {}
    }
    
    // Reset stats
    this.stats = {
      hits: 0,
      misses: 0,
      securityBlocks: 0,
      encryptionOps: 0
    };
  }

  /**
   * Clear only sensitive data from memory
   */
  clearSensitiveData() {
    console.log('🔒 Clearing sensitive data from memory');
    
    // Clear all from memory to be safe
    this.memoryCache.clear();
    
    // Keep only PUBLIC data in session/persistent storage
    // (INTERNAL and CONFIDENTIAL are already cleared or encrypted)
  }

  /**
   * Get security statistics
   */
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    return {
      ...this.stats,
      hitRate: total > 0 ? ((this.stats.hits / total) * 100).toFixed(2) + '%' : '0%',
      missRate: total > 0 ? ((this.stats.misses / total) * 100).toFixed(2) + '%' : '0%',
      securityLevel: 'ENHANCED'
    };
  }

  /**
   * Log security audit
   */
  logSecurityAudit() {
    console.log('🔒 SECURITY AUDIT:');
    console.table(this.getStats());
    console.log('Encryption Key Initialized:', !!this.encryptionKey);
    console.log('Session ID:', this._getSessionId().substring(0, 8) + '...');
  }
}

// Export singleton instance
export const secureCacheManager = new SecureCacheManager();

// Export class for testing
export default SecureCacheManager;

// Convenience functions for common use cases (with security)
export const getCachedUserProfile = (userId, fetchFn) => 
  secureCacheManager.get(`user_display_${userId}`, fetchFn); // Only display data

export const getCachedCompanyName = (companyId, fetchFn) => 
  secureCacheManager.get(`company_name_${companyId}`, fetchFn); // Only name

export const getCachedDashboardSummary = (companyId, fetchFn) => 
  secureCacheManager.get(`dashboard_summary_${companyId}`, fetchFn); // Summary only

// IMPORTANT: Never use these for sensitive data!
// secureCacheManager will automatically block restricted data

// Clear cache on logout
export const clearCacheOnLogout = () => {
  secureCacheManager.clearAll();
  // Dispatch logout event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('user_logout'));
  }
};

// React hook
export const useSecureCache = () => {
  return {
    get: secureCacheManager.get.bind(secureCacheManager),
    set: secureCacheManager.set.bind(secureCacheManager),
    invalidate: secureCacheManager.invalidate.bind(secureCacheManager),
    clearAll: secureCacheManager.clearAll.bind(secureCacheManager),
    getStats: secureCacheManager.getStats.bind(secureCacheManager),
    logSecurityAudit: secureCacheManager.logSecurityAudit.bind(secureCacheManager)
  };
};




